import { useEffect, useState, useCallback } from "react";
import { Spin } from "antd";
import { useStores } from "Hooks/useStore";
import { observer } from "mobx-react-lite";
import { Icon, Select, Tabs } from "Components";
import { SystemRoleType } from 'Services/Api/Roles/Types';
import Header from "PhishingManager/components/Header";
import GridReportCard from "./components/GridReportCard";
import FlexReportCard from "./components/FlexReportCard";
import OverAllCard from "./components/OverallCard";
import ReportList from "./components/ReportList";
import { TABLE_TABS } from "./CampaignReport.constant";
import { capitalize, downloadBlob, formatDateTime } from "PhishingManager/utils";
import LearnMoreModal from "./components/LearnMoreModal";
import { getCoreMetricsData, getIndividualUserData, getOverAllReport, getPhishingCampaignsForTenant, getPropertiesMetrics, getReportData } from "PhishingManager/service/index.Service";
import { CORE_METRICS_DATA, OVERALL_REPORT_DATA, PropertyData, TABLE_STATE } from "./CampaignReport.type";
import InfinityScrollWrapper from "Components/InfinityScroll/InfinityScrollWrapper";
import "./CampaignReport.scss";

type CampaginReportProps = {
    showOverallReport: boolean,
    campaignId: number | null,
    tenentId: number | null,
    resetCampaginData: () => void;
}

type CAMPAIGN_STATE = {
    id: number;
    venzaTenentId: number;
    campaigns: { label: string, value: number }[],
    currentPage: number,
    totalCount: number
}

type OverAllState = {
    loading: boolean;
    data: OVERALL_REPORT_DATA | null;
}

type CAMPAIGN_REPORT = {
    loading: boolean;
    coreMetrics: CORE_METRICS_DATA | null;
}

const initCampaignState: CAMPAIGN_STATE = { id: 0, venzaTenentId: 0, campaigns: [], totalCount: 0, currentPage: 1 };
const initOverAllResult: OverAllState = { loading: false, data: null };
const initCampaignReport: CAMPAIGN_REPORT = { loading: false, coreMetrics: null };
const initTableState: TABLE_STATE = {
    individualData: [],
    propertyData: [],
    propertyExtraData: {
        averagePassRate: 0,
        bestPerforming: {
            name: "",
            passRate: 0
        },
        belowPassRate: 0
    },
    isLoading: false,
    isPropertyLoading: false
};

const CampaignReport = ({ showOverallReport, campaignId, tenentId, resetCampaginData }: CampaginReportProps) => {
    const [campaignState, setCampaginState] = useState<CAMPAIGN_STATE>({...initCampaignState});
    const [overAllResult, setOverallResult] = useState<OverAllState>({...initOverAllResult});
    const [campaignReport, setCampaginReport] = useState<CAMPAIGN_REPORT>({...initCampaignReport});
    const [tableState, setTableState] = useState<TABLE_STATE>({...initTableState});
    const [activeTab, setActiveTab] = useState("individual");
    const [showLearnMore, setShowLearnMore] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [open, setOpen] = useState(false);
    const [searchCampaign, setSearchCampaign] = useState("");
    const { id, venzaTenentId, campaigns, totalCount, currentPage } = campaignState;

	const { authStore, systemsStore } = useStores();
	const { tenantList: tenants, tenantLoading: loading } = systemsStore;

    const isSuperAdmin = authStore.currentUser?.primarySystemRole?.role === SystemRoleType.SuperAdmin;

    const resetData = (newTenentId: number) => {
        setCampaginState({...initCampaignState, venzaTenentId: newTenentId });
        setOverallResult({...initOverAllResult});
        setCampaginReport({...initCampaignReport});
        setTableState({...initTableState});
        setActiveTab("individual");
    }

    const dropdownRender = useCallback(() => {
        const hasMore = campaigns.length < totalCount;
        const filteredList = searchCampaign ? campaigns.filter(item => item.label.toLowerCase().includes(searchCampaign.toLowerCase())) : campaigns;
        return <div onMouseDown={(e) => e.preventDefault()} >
        {filteredList.length === 0 && !isLoading ? (
            <div className="no-option">No campaign found</div>
        ) : (
            <InfinityScrollWrapper
                hasMore={hasMore}
                dataLength={campaigns.length}
                onLoadMore={() => {
                    if (!isLoading && hasMore) setCampaginState(prev => ({...prev, currentPage: prev.currentPage + 1 }));
                }}
                loading={isLoading}
                height={200}
                scrollableId="campagin-select-dropdown"
                loaderText="Loading campaigns...">
                {filteredList.map(el => (
                    <span className={`select-option ${el.value === id ? "active" : ""}`} onClick={() => {
                        setOpen(false);
                        setCampaginState(prev => ({...prev, id: el.value }));
                    }}>
                        {el.label}
                    </span>
                ))}
            </InfinityScrollWrapper>
        )}</div>
    }, [isLoading, id, currentPage, campaigns, searchCampaign, totalCount]);

    const getCompletedCampaigns = useCallback(async () => {
        try {
            setIsLoading(true);
            const response = await getPhishingCampaignsForTenant(venzaTenentId, currentPage, 25, "reporting", "full");
            const currentIdFromUrl = Number(id);
            let foundCurrent = false;

            const newData = (response?.data || []).reduce((acc: { label: string, value: number }[], item: any) => {
                const value = item?.id;
                const label = item?.name || 'Unnamed Campaign';

                if (value === currentIdFromUrl) foundCurrent = true;
                if (value) acc.push({ value, label });
                return acc;
            }, []);
            const campaignsList = [...campaigns, ...newData];
            const finalId = foundCurrent ? currentIdFromUrl : campaignsList?.[0]?.value || 0;

            setCampaginState(prev => ({
                ...prev,
                id: finalId,
                campaigns: campaignsList,
                totalCount: response?.meta?.total || 0
            }));
        } catch (error) {
            setCampaginState(prev => ({ ...prev, campaigns: [], currentPage: 1, totalCount: 0 }));
        } finally {
            setIsLoading(false);
        }
    }, [venzaTenentId, currentPage, campaigns, id]); // Dependencies ensure sync with URL and Tenant

    const downloadReport = useCallback(async () => {
        try {
            if(isDownloading || !venzaTenentId || !id) return;
            setIsDownloading(true);
            const responseBlob = await getReportData(venzaTenentId, id);
            if(responseBlob?.blob) downloadBlob(responseBlob.blob, responseBlob?.fileName || `download-report-${id}`);
        } catch (error) { } finally {
            setIsDownloading(false);
        }
    }, [venzaTenentId, id, isDownloading]); // Dependencies ensure sync with URL and Tenant

    const getOverAllData = useCallback(async () => {
        try {
            setOverallResult(prev => ({ ...prev, loading: true }));
            const response = await getOverAllReport(venzaTenentId);
            const data = response?.data || null;

            if (data?.last_campaign) {
                const launchDate = data.last_campaign.launch_date;
                // Corrected moment format to 'MM/DD/YYYY' (standard for reports)
                const formattedDate = launchDate ? formatDateTime(launchDate, "M/D/YYYY") : '';
                data.last_campaign.completeTitle = `${data.last_campaign.name ?? ''} | ${formattedDate}`;
            }

            setOverallResult(prev => ({ ...prev, data }));
        } catch (error) {
            setOverallResult(prev => ({ ...prev, data: null }));
        } finally {
            setOverallResult(prev => ({ ...prev, loading: false }));
        }
    }, [venzaTenentId]); // Only changes if the tenant changes

    const getCoreMetrics = useCallback(async () => {
        // Optimization: Don't fetch if no campaign ID exists
        if (!id) return;

        try {
            setCampaginReport(prev => ({ ...prev, loading: true }));
            const response = await getCoreMetricsData(venzaTenentId, id);
            const data = response?.data || null;
            if (data) {
                const { 
                    email_sent = 0, 
                    email_opened = 0, 
                    clicked_link_count = 0, 
                    submitted_data_count = 0,
                    property_count = 0,
                    launch_date = ''
                } = data;
                const formattedDate = launch_date ? formatDateTime(launch_date, "M/D/YYYY") : '';
                // Helper: Safely calculate ratio and round to 2 decimal places
                const calculateSafeRate = (num: number, den: number) => den > 0 ? Number(((num / den) * 100).toFixed(1)) : 0;
            
                // Assigning calculated rates
                Object.assign(data, {
                    emailOpenPercentage: calculateSafeRate(email_opened, email_sent),
                    clickPercentage: calculateSafeRate(clicked_link_count, email_sent),
                    submittedDataPercentage: calculateSafeRate(submitted_data_count, email_sent),
                    launch_date: formattedDate,
                    target_count: property_count
                });
            }
            setCampaginReport(prev => ({ ...prev, coreMetrics: data }));
        } catch (error) {
            setCampaginReport(prev => ({ ...prev, coreMetrics: null }));
        } finally {
            setCampaginReport(prev => ({ ...prev, loading: false }));
        }
    }, [venzaTenentId, id]); // Re-run if tenant or selected campaign ID changes

    const getProperties = useCallback(async () => {
        try {
            setTableState(prev => ({...prev, isPropertyLoading: true }));
            const response = await getPropertiesMetrics(venzaTenentId, id);
            const properties = response?.data || [];
            const avg = Number((properties.reduce((s: number, g: PropertyData) => s + (g.pass_rate || 0), 0) / (properties.length || 1)).toFixed(1));
            const best = properties.reduce((a: PropertyData, b: PropertyData) => (b.pass_rate > a.pass_rate ? b : a), properties[0] || {});
            const below = properties.filter((g: PropertyData) => (g.pass_rate || 0) < 90).length;
            const propertyExtraData = {
                averagePassRate: avg,
                bestPerforming: {
                    name: best?.group_name || "-",
                    passRate: +(best?.pass_rate || 0).toFixed(1) || 0,
                },
                belowPassRate: below
            };
            setTableState(prev => ({...prev, propertyData: properties, propertyExtraData }));
        } catch (error) {
            setTableState(prev => ({ ...prev, propertyData: [], propertyExtraData: { ...initTableState.propertyExtraData } }));
        } finally {
            setTableState(prev => ({...prev, isPropertyLoading: false }));
        }
    }, [venzaTenentId, id]);

    const getIndividualData = useCallback(async () => {
        try {
            setTableState(prev => ({...prev, isLoading: true }));
            const response = await getIndividualUserData(venzaTenentId, id);
            const individualData = response?.data?.results || [];
            setTableState(prev => ({...prev, individualData }));
        } catch (error) {
            setTableState(prev => ({ ...prev, individualData: [] }));
        } finally {
            setTableState(prev => ({...prev, isLoading: false }));
        }
    }, [venzaTenentId, id]);

    useEffect(() => {
        if(!open && searchCampaign.length) setSearchCampaign("");
    }, [open]);

    useEffect(() => {
        if(venzaTenentId) getCompletedCampaigns();
    }, [currentPage, venzaTenentId])

    // when we get a suitable tenant we fetch different api associated with tenentID and also on tenant change.
    useEffect(() => {
        if (id && venzaTenentId){
            getCoreMetrics();
            getProperties();
            getIndividualData();
        }
    }, [id])

    // when we get a suitable tenant we fetch different api associated with tenentID and also on tenant change.
    useEffect(() => {
        if (venzaTenentId && showOverallReport) getOverAllData();
    }, [venzaTenentId])

    // when we get all the tenants here we set the tenantId.
    useEffect(() => {
        if (tenants.length) {
            let venzaTenentId = Number(tenentId);
            if (!venzaTenentId) venzaTenentId = tenants[0].value;
            setCampaginState(prev => ({ ...prev, venzaTenentId }));
        }
    }, [tenants])

    // checks for overallreport if we needs all the tenants or it is a report for specific campaign.
    useEffect(() => {
        if (showOverallReport) setIsLoading(true);
        else setCampaginState(prev => ({ ...prev, venzaTenentId: Number(tenentId), id: Number(campaignId) }));
    }, []);

    // reset campagin data if we have any selected campagins
    useEffect(() => {
        return resetCampaginData;
    }, []);

    return <div className="campaign-report-container">
        {showOverallReport && <Spin spinning={loading || overAllResult.loading}>
            <div className="overall-section">
                <Header title="Overall Results" description="" parentClass='phishing-header'>
                    <div className="pm-dropdown-section">
                        <label htmlFor="campagin-select" className="pm-client-label">
                            Tenant
                        </label>
                        <Select
                            id="campagin-select"
                            placeholder="Select a Tenant"
                            dropdownClassName="campaign-setup-filter-option-select"
                            value={campaignState.venzaTenentId}
                            loading={loading}
                            onChange={resetData}
                            showSearch
							optionFilterProp="label"
                            disabled={!isSuperAdmin || loading}
                            suffixIcon={<Icon type="campaignDropdown" />}
                            options={tenants}
                            getPopupContainer={(trigger) => trigger.parentElement}
                        />
                    </div>
                </Header>
                <div className="cards-flex">
                    <OverAllCard label="OVERALL" title="Overall Pass Rate" desc="Combined rate of all phishing simulations" data={[{ label: "", count: overAllResult?.data?.overall_pass_rate || 0, showTag: true }]} />
                    <OverAllCard label="RECENT ACTIVITY" title="Last Campaign" desc={overAllResult?.data?.last_campaign?.completeTitle || ''} data={[{ label: "Pass Rate", count: overAllResult?.data?.last_campaign?.pass_rate || 0, showTag: false }, { label: "Fail Rate", count: overAllResult?.data?.last_campaign?.fail_rate || 0, showTag: false }]} />
                    <OverAllCard label="SUMMARY" title="Phishing Campaigns" desc="Total campaigns in the last 12 months" data={[{ label: "", count: overAllResult?.data?.total_campaigns || 0, showTag: false }]} />
                </div>
            </div>
        </Spin>}
        <div className="campaign-section">
            <Spin spinning={isLoading || campaignReport.loading} wrapperClassName="loading-container">
                <Header title="Campaign Report" description="" parentClass='phishing-header'>
                    <div className="pm-dropdown-section">
                        <Select
                            placeholder="Select a Campaign"
                            value={campaignState.id || undefined}
                            showSearch
                            open={open}
                            onDropdownVisibleChange={setOpen}
                            disabled={isLoading}
                            suffixIcon={<Icon type="campaignDropdown" />}
                            options={campaigns}
                            searchValue={searchCampaign}
                            onSearch={setSearchCampaign}
                            getPopupContainer={(trigger) => trigger.parentElement}
                            dropdownRender={dropdownRender}
                        />
                    </div>

                    <Spin spinning={isDownloading}>
                        <Icon type="download" className="icon-btn download-btn" onClick={downloadReport} />
                    </Spin>
                    <Icon type="campaignInfo" className="icon-btn info-btn" onClick={() => setShowLearnMore(true)} />
                </Header>

                <div className="report-card">
                    <span className="card-title">Overall Results</span>
                    <div className="cards-grid">
                        <GridReportCard label="Name" desc={campaignReport?.coreMetrics?.campaign || ''} iconType="feedback" />
                        <GridReportCard label="Date" desc={campaignReport?.coreMetrics?.launch_date || ''} iconType="calendarReport" />
                        <GridReportCard label={"Properties"} desc={`${campaignReport?.coreMetrics?.target_count || 0}`} iconType="properties" />
                        <GridReportCard label="Target Users" desc={`${campaignReport?.coreMetrics?.campaign_users_count || 0}`} iconType="usersReport" />
                    </div>
                </div>

                <div className="report-card">
                    <span className="card-title">Core Metrics</span>
                    <div className="cards-flex">
                        <FlexReportCard isPassCard={true} iconType="shield" label="Pass Rate" score={campaignReport?.coreMetrics?.pass_rate || 0} desc="Users who passed" toolTipText="Percentage of users who successfully passed the phishing test based on their interaction with the email." />
                        <FlexReportCard isPassCard={false} iconType="emailSent" label="Email Sent" score={campaignReport?.coreMetrics?.email_sent || 0} desc="Emails Successfully Sent" toolTipText="Total number of emails sent. Some emails may not have been sent due to delivery restrictions." />
                        <FlexReportCard isPassCard={false} iconType="emailOpen" label="Opened Email" score={campaignReport?.coreMetrics?.email_opened || 0} desc={`${campaignReport?.coreMetrics?.emailOpenPercentage || 0}% open rate`} toolTipText="Number of users who opened the phishing email at least once." />
                        <FlexReportCard isPassCard={false} iconType="userClick" label="Clicked Link" score={campaignReport?.coreMetrics?.clicked_link_count || 0} desc={`${campaignReport?.coreMetrics?.clickPercentage || 0}% click rate`} toolTipText="Number of users who clicked on a link in the email." />
                        <FlexReportCard isPassCard={false} iconType="submittedData" label="Submitted Data" score={campaignReport?.coreMetrics?.submitted_data_count || 0} desc={`${campaignReport?.coreMetrics?.submittedDataPercentage || 0}% submission rate`} toolTipText="Number of users who submitted information after interacting with the email." />
                    </div>
                </div>
            </Spin>
            <div className="tab-section">
                <Tabs activeKey={activeTab} onChange={setActiveTab}>
                    {TABLE_TABS.map(item => <Tabs.TabPane tab={capitalize(item)} key={item} destroyInactiveTabPane>
                        <ReportList tab={activeTab} tableState={tableState} />
                    </Tabs.TabPane>)}
                </Tabs>
            </div>
        </div>
        {showLearnMore && <LearnMoreModal open={showLearnMore} onClose={() => setShowLearnMore(false)} onConfirm={() => setShowLearnMore(false)} />}
    </div>
}

export default observer(CampaignReport);